import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { endOfMonth, format, startOfMonth, subDays, subMonths } from "date-fns";
import Admin from "../layout/Admin";
import spinner from "../layout/spinner.gif";
import { getAdReport } from "../../actions/ads";
import { formatCount, weekdayOf } from "../dashboard/incomeLabels";
import {
	AdTabs,
	HOUSE_CAMPAIGN,
	PAGE_LABELS,
	SIZE_LABELS,
	card,
	durationText,
	share,
} from "./adLabels";

// Proof of play: how often and how long each ad was on each kiosk's screen, from
// what the kiosks count (GET /api/ads/report). Picked for one campaign it is what
// its advertiser is shown, with a CSV for the invoice. For all of them it also tells
// how much of the screen time was sold, against the kiosks' own ad. Days are
// Mongolian days. Kept on the page, not in the store.

const dayOf = (date) => format(date, "yyyy-MM-dd");

const RANGES = [
	{ key: "7days", label: "7 хоног", range: (now) => [subDays(now, 6), now] },
	{ key: "30days", label: "30 хоног", range: (now) => [subDays(now, 29), now] },
	{ key: "month", label: "Энэ сар", range: (now) => [startOfMonth(now), now] },
	{
		key: "lastMonth",
		label: "Өмнөх сар",
		range: (now) => [startOfMonth(subMonths(now, 1)), endOfMonth(subMonths(now, 1))],
	},
];
const daysOf = (option) => option.range(new Date()).map(dayOf);

const labelClass = "block text-xs font-medium text-gray-500 dark:text-gray-400";
const fieldClass =
	"block h-10 mt-1 text-sm text-gray-700 bg-white border-gray-300 rounded-lg dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600";
const th = "px-4 py-3";
const td = "px-4 py-2 text-sm";
const num = td + " text-right tabular-nums";

const empty = () => ({ plays: 0, completes: 0, shownSec: 0, largeSec: 0, paidSec: 0 });
const add = (total, row) => {
	total.plays += row.plays;
	total.completes += row.completes;
	total.shownSec += row.shownSec;
	if (row.size === "large") total.largeSec += row.shownSec;
	if (row.campaignId !== HOUSE_CAMPAIGN) total.paidSec += row.shownSec;
	return total;
};
const groupBy = (rows, keyOf) => {
	const groups = new Map();
	for (const row of rows) {
		const key = keyOf(row);
		groups.set(key, add(groups.get(key) || empty(), row));
	}
	return groups;
};

const csvCell = (value) => {
	const text = String(value === undefined || value === null ? "" : value);
	return /[",;\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const StatCard = ({ title, value, note }) => (
	<div className={"p-5 " + card}>
		<p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
		<p className="mt-2 text-2xl font-semibold tracking-tight text-gray-800 truncate dark:text-gray-100">{value}</p>
		<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{note}</p>
	</div>
);

// The columns every table has
const Totals = ({ total }) => (
	<>
		<td className={num}>{formatCount(total.plays)}</td>
		<td className={num}>
			{formatCount(total.completes)}
			<span className="ml-1 text-xs text-gray-400">{share(total.completes, total.plays)}%</span>
		</td>
		<td className={num}>{durationText(total.shownSec - total.largeSec)}</td>
		<td className={num}>{durationText(total.largeSec)}</td>
	</>
);
const TotalsHead = () => (
	<>
		<th className={th + " text-right"}>Тоглолт</th>
		<th className={th + " text-right"}>Бүрэн тоглосон</th>
		<th className={th + " text-right"}>{SIZE_LABELS.small}</th>
		<th className={th + " text-right"}>{SIZE_LABELS.large}</th>
	</>
);

const Table = ({ title, note, head, children }) => (
	<section className={"mb-6 overflow-hidden " + card}>
		<div className="px-5 pt-4 pb-3">
			<h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">{title}</h2>
			{note && <p className="text-sm text-gray-500 dark:text-gray-400">{note}</p>}
		</div>
		<div className="w-full overflow-x-auto">
			<table className="w-full whitespace-nowrap">
				<thead>
					<tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-t border-b dark:border-gray-700 bg-gray-50 dark:text-gray-400 dark:bg-gray-800">
						{head}
					</tr>
				</thead>
				<tbody className="text-gray-700 bg-white divide-y dark:divide-gray-700 dark:bg-gray-800 dark:text-gray-300">
					{children}
				</tbody>
			</table>
		</div>
	</section>
);

const AdReport = ({ getAdReport }) => {
	const [[appliedFrom, appliedTo], setApplied] = useState(daysOf(RANGES[0]));
	const [from, setFrom] = useState(appliedFrom);
	const [to, setTo] = useState(appliedTo);
	const [report, setReport] = useState(null);
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(true);
	// A campaign's id, HOUSE_CAMPAIGN, or "" for every ad
	const [selected, setSelected] = useState("");

	useEffect(() => {
		let left = false;
		setFrom(appliedFrom);
		setTo(appliedTo);
		setLoading(true);
		getAdReport(appliedFrom, appliedTo).then((result) => {
			if (left || result.stale) return;
			setLoading(false);
			setError(result.error || null);
			if (result.report) setReport(result.report);
		});
		return () => {
			left = true;
		};
	}, [appliedFrom, appliedTo, getAdReport]);

	const names = useMemo(() => {
		const campaigns = new Map();
		const creatives = new Map([["house-reclam", "reclam.mp4"]]);
		const printers = new Map();
		if (report) {
			report.campaigns.forEach((campaign) => {
				campaigns.set(campaign._id, campaign);
				campaign.creatives.forEach((creative) => creatives.set(creative._id, creative.name));
			});
			report.printers.forEach((printer) => printers.set(printer._id, printer));
		}
		return { campaigns, creatives, printers };
	}, [report]);

	const campaignName = (id) =>
		id === HOUSE_CAMPAIGN ? "Өөрсдийн зар" : (names.campaigns.get(id) || {}).name || "Устгагдсан кампанит ажил";
	const kioskOf = (id) => names.printers.get(id) || { name: "Устгагдсан киоск", location: "" };

	const rows = useMemo(
		() => (report ? report.rows.filter((row) => !selected || row.campaignId === selected) : []),
		[report, selected],
	);
	const total = useMemo(() => rows.reduce(add, empty()), [rows]);
	const byKiosk = useMemo(
		() => [...groupBy(rows, (row) => row.printerId)].sort((a, b) => b[1].shownSec - a[1].shownSec),
		[rows],
	);
	const byDay = useMemo(() => groupBy(rows, (row) => row.day), [rows]);
	const byCampaign = useMemo(
		() => [...groupBy(rows, (row) => row.campaignId)].sort((a, b) => b[1].shownSec - a[1].shownSec),
		[rows],
	);
	// Every campaign in the range, and the ones on the list that did not play
	const campaignOptions = report
		? [
				...new Set([
					...report.campaigns.map((campaign) => campaign._id),
					...report.rows.map((row) => row.campaignId),
				]),
		  ].filter((id) => id !== HOUSE_CAMPAIGN)
		: [];

	const days = report ? report.days : [];
	const active = RANGES.find((option) => {
		const [start, end] = daysOf(option);
		return start === appliedFrom && end === appliedTo;
	});
	const submitCustom = (e) => {
		e.preventDefault();
		if (from && to && from <= to) setApplied([from, to]);
	};

	const download = () => {
		const header = [
			"Огноо",
			"Киоск",
			"Байршил",
			"Кампанит ажил",
			"Сурталчлагч",
			"Бүтээл",
			"Хэмжээ",
			"Хуудас",
			"Тоглолт",
			"Бүрэн тоглосон",
			"Харагдсан секунд",
		];
		const lines = rows.map((row) => {
			const kiosk = kioskOf(row.printerId);
			const campaign = names.campaigns.get(row.campaignId);
			return [
				row.day,
				kiosk.name,
				kiosk.location,
				campaignName(row.campaignId),
				campaign ? campaign.advertiser : "",
				names.creatives.get(row.creativeId) || row.creativeId,
				SIZE_LABELS[row.size] || row.size,
				PAGE_LABELS[row.page] || row.page,
				row.plays,
				row.completes,
				row.shownSec,
			];
		});
		// The byte order mark lets Excel read the Cyrillic
		const text = "﻿" + [header, ...lines].map((line) => line.map(csvCell).join(",")).join("\r\n");
		const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
		const link = document.createElement("a");
		const label = selected ? campaignName(selected).replace(/[\\/:*?"<>|]+/g, " ").trim() : "buh-zar";
		link.href = url;
		link.download = `${label} ${appliedFrom}_${appliedTo}.csv`;
		link.click();
		URL.revokeObjectURL(url);
	};

	return (
		<Admin>
			<div className="min-w-0 pb-10">
				<div className="mt-6 mb-4">
					<h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-200">Реклам</h2>
					<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
						Зар бүр киоск бүрийн дэлгэцэнд хэдэн удаа, хэр удаан харагдсан. Дэлгэц нээлттэй байх үеийн бодит
						хугацааг л тоолно.
					</p>
				</div>
				<AdTabs active="/ad-report" />

				<section className={"p-4 mb-6 " + card}>
					<div className="flex flex-wrap items-end gap-x-6 gap-y-4">
						<div>
							<span className={labelClass + " mb-1"}>Хугацаа</span>
							<div className="flex flex-wrap gap-2">
								{RANGES.map((option) => (
									<button
										type="button"
										key={option.key}
										onClick={() => setApplied(daysOf(option))}
										aria-pressed={active === option}
										className={`h-10 px-4 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-300 ${
											active === option
												? "text-white bg-purple-600 shadow-sm"
												: "text-gray-600 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
										}`}
									>
										{option.label}
									</button>
								))}
							</div>
						</div>
						<label className={labelClass}>
							Зар
							<select value={selected} onChange={(e) => setSelected(e.target.value)} className={fieldClass + " w-64"}>
								<option value="">Бүх зар</option>
								{campaignOptions.map((id) => (
									<option key={id} value={id}>
										{campaignName(id)}
									</option>
								))}
								<option value={HOUSE_CAMPAIGN}>Өөрсдийн зар</option>
							</select>
						</label>
						<form onSubmit={submitCustom} className="flex flex-wrap items-end gap-3 xl:ml-auto">
							<label className={labelClass}>
								Эхлэх огноо
								<input
									type="date"
									value={from}
									max={to}
									onChange={(e) => setFrom(e.target.value)}
									className={fieldClass + " w-full"}
								/>
							</label>
							<label className={labelClass}>
								Дуусах огноо
								<input
									type="date"
									value={to}
									min={from}
									max={dayOf(new Date())}
									onChange={(e) => setTo(e.target.value)}
									className={fieldClass + " w-full"}
								/>
							</label>
							<button
								type="submit"
								disabled={loading || !from || !to || from > to}
								className="h-10 px-5 text-sm font-semibold text-white bg-gray-800 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-800 dark:bg-gray-100"
							>
								Харах
							</button>
						</form>
					</div>
				</section>

				{error && (
					<div className="px-4 py-3 mb-6 text-sm text-red-700 border border-red-200 rounded-lg bg-red-50 dark:text-red-200 dark:bg-red-900 dark:border-red-700">
						{error}
						{report && " Өмнөх мэдээллийг харуулж байна."}
					</div>
				)}

				{!report ? (
					loading && (
						<img src={spinner} className="my-6" style={{ width: "50px", margin: "auto", display: "block" }} alt="Loading..." />
					)
				) : (
					// The last report stays, dimmed, while the next one loads
					<div className={"transition-opacity " + (loading ? "opacity-50" : "")}>
						<div className="flex flex-wrap items-center justify-between gap-3 mb-3">
							<p className="text-sm text-gray-500 dark:text-gray-400">
								{days[0]} — {days[days.length - 1]} · {days.length} өдөр
								{selected && ` · ${campaignName(selected)}`}
							</p>
							<button
								type="button"
								onClick={download}
								disabled={rows.length === 0}
								className={
									"inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600" +
									(rows.length === 0 ? " opacity-50 cursor-not-allowed" : "")
								}
							>
								<i className="mr-2 fas fa-file-csv" />
								CSV татах
							</button>
						</div>

						<div className="grid gap-4 mb-6 sm:grid-cols-2 xl:grid-cols-4">
							<StatCard
								title="Тоглолт"
								value={formatCount(total.plays)}
								note={`${byKiosk.length} киоскт`}
							/>
							<StatCard
								title="Бүрэн тоглосон"
								value={formatCount(total.completes)}
								note={`Тоглолтын ${share(total.completes, total.plays)}%`}
							/>
							<StatCard
								title="Харагдсан хугацаа"
								value={durationText(total.shownSec)}
								note={
									selected
										? `Өдөрт дунджаар ${durationText(total.shownSec / Math.max(days.length, 1))}`
										: `Зарагдсан нь ${share(total.paidSec, total.shownSec)}%`
								}
							/>
							<StatCard
								title={SIZE_LABELS.large}
								value={durationText(total.largeSec)}
								note={`Харагдсан хугацааны ${share(total.largeSec, total.shownSec)}%`}
							/>
						</div>

						{rows.length === 0 ? (
							<div className={"p-10 text-center text-sm text-gray-500 dark:text-gray-400 " + card}>
								Энэ хугацаанд тоглолт бүртгэгдээгүй байна.
							</div>
						) : (
							<>
								{!selected && (
									<Table
										title="Зараар"
										note="Мөрийг дарж тухайн зарын тайланг харна"
										head={
											<>
												<th className={th}>Зар</th>
												<TotalsHead />
												<th className={th + " text-right"}>Хугацааны хувь</th>
											</>
										}
									>
										{byCampaign.map(([id, item]) => (
											<tr
												key={id}
												onClick={() => setSelected(id)}
												className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
											>
												<td className={td}>
													<span className="font-medium">{campaignName(id)}</span>
													{names.campaigns.get(id) && names.campaigns.get(id).advertiser && (
														<span className="block text-xs text-gray-500 dark:text-gray-400">
															{names.campaigns.get(id).advertiser}
														</span>
													)}
												</td>
												<Totals total={item} />
												<td className={num}>{share(item.shownSec, total.shownSec)}%</td>
											</tr>
										))}
									</Table>
								)}

								<Table
									title="Киоскоор"
									note={selected ? null : "Зарагдсан: өөрсдийн зараас бусад зарын харагдсан хугацааны хувь"}
									head={
										<>
											<th className={th}>Киоск</th>
											<TotalsHead />
											{!selected && <th className={th + " text-right"}>Зарагдсан</th>}
										</>
									}
								>
									{byKiosk.map(([id, item]) => (
										<tr key={id}>
											<td className={td}>
												<span className="font-medium">{kioskOf(id).name}</span>
												<span className="block text-xs text-gray-500 dark:text-gray-400">
													{kioskOf(id).location || "Байршилгүй"}
												</span>
											</td>
											<Totals total={item} />
											{!selected && <td className={num}>{share(item.paidSec, item.shownSec)}%</td>}
										</tr>
									))}
								</Table>

								<Table
									title="Өдрөөр"
									head={
										<>
											<th className={th}>Өдөр</th>
											<TotalsHead />
										</>
									}
								>
									{days.map((day) => (
										<tr key={day}>
											<td className={td}>
												{day}
												<span className="ml-2 text-xs text-gray-400">{weekdayOf(day)}</span>
											</td>
											<Totals total={byDay.get(day) || empty()} />
										</tr>
									))}
								</Table>
							</>
						)}
					</div>
				)}
			</div>
		</Admin>
	);
};

StatCard.propTypes = {
	title: PropTypes.string.isRequired,
	value: PropTypes.string.isRequired,
	note: PropTypes.string.isRequired,
};

Totals.propTypes = {
	total: PropTypes.object.isRequired,
};

Table.propTypes = {
	title: PropTypes.string.isRequired,
	note: PropTypes.string,
	head: PropTypes.node.isRequired,
	children: PropTypes.node,
};

AdReport.propTypes = {
	getAdReport: PropTypes.func.isRequired,
};

export default connect(null, { getAdReport })(AdReport);
